// FranchiseeUpdateComponent.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import { Autocomplete, InputAdornment, IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import styles from "./FranchiseeUpdateComponent.module.css";
import leftArrow from "../../../assests/leftArrow.svg";
import ClearIcon from "@mui/icons-material/Clear";

const FranchiseeUpdateComponent = (props) => {
  const { cohortId } = useParams();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [selectedCohortType, setSelectedCohortType] = useState(null);

  // Cohort type mapping
  const cohortTypeMapping = {
    13: "PDF",
    4: "FOCO(City)",
    2: "FOFO(Franchise)",
  };

  const cohortTypeOptions = Object.entries(cohortTypeMapping).map(
    ([id, name]) => ({ id, name })
  );

  // Set selected cohort type when formDataFranchisee changes
  useEffect(() => {
    if (props.formDataFranchisee?.cohortTypeId) {
      const cohortTypeIdStr = String(props.formDataFranchisee.cohortTypeId);
      const matchingCohortType = cohortTypeOptions.find(
        (option) => option.id === cohortTypeIdStr
      );
      setSelectedCohortType(matchingCohortType || null);
    } else {
      setSelectedCohortType(null);
    }
  }, [props.formDataFranchisee?.cohortTypeId, props.formDataFranchisee?.type]);

  // Filter warehouses
  const filteredWarehouses = props.warehouseList
    ? props.warehouseList.filter(
        (warehouse) =>
          warehouse.name?.toLowerCase().includes(searchInput.toLowerCase()) ||
          warehouse.id?.toString().includes(searchInput)
      )
    : [];

  const selectedWarehouse =
    props.warehouseList?.find(
      (wh) => wh.id === props.formDataFranchisee?.warehouseId
    ) || null;

  const goback = () => navigate(-1);

  return (
    <div className={styles.franchiseeMain}>
      <Sidebar />

      <div className={styles.franchiseeContent}>
        {/* ============ HEADER ============ */}
        <div className={styles.franchiseeHeader}>
          <button
            className={styles.backButton}
            onClick={goback}
            type="button"
          >
            <img src={leftArrow} alt="Back" />
          </button>
          <div className={styles.titleBlock}>
            <h2 className={styles.pageTitle}>Edit Franchisee</h2>
            <p className={styles.pageSubtitle}>
              Update franchisee information
            </p>
          </div>
        </div>

        {/* ============ FULL-WIDTH WHITE WRAPPER ============ */}
        <div className={styles.franchiseeFormContainer}>
          {/* Inner content wrapper */}
          <div className={styles.franchiseeFormInner}>
            {/* Card Header */}
            <div className={styles.cardHeader}>
              <div className={styles.cardIconCircle}>
                <span>🏢</span>
              </div>
              <div className={styles.cardHeaderText}>
                <div className={styles.cardTitle}>Franchisee Details</div>
                <div className={styles.cardSubtitle}>
                  Update the franchisee configuration
                </div>
              </div>
            </div>

            {/* Form Section */}
            <div className={styles.formSection}>
            

              <div className={styles.franchiseeFormBox}>
                <div className={styles.formField}>
                  <label className={styles.fieldLabel}>
                    Name <span className={styles.required}>*</span>
                  </label>
                  <TextField
                    fullWidth
                    placeholder="Enter franchisee name"
                    name="name"
                    value={props.formDataFranchisee?.name || ""}
                    onChange={props.handleUpdateFranchisee}
                    variant="outlined"
                    size="small"
                    className={styles.inputField}
                  />
                </div>

                <div className={styles.formField}>
                  <label className={styles.fieldLabel}>
                    Description <span className={styles.required}>*</span>
                  </label>
                  <TextField
                    fullWidth
                    placeholder="Enter description"
                    name="description"
                    value={props.formDataFranchisee?.description || ""}
                    onChange={props.handleUpdateFranchisee}
                    variant="outlined"
                    size="small"
                    className={styles.inputField}
                  />
                </div>

                <div className={styles.formField}>
                  <label className={styles.fieldLabel}>
                    Cohort Type <span className={styles.required}>*</span>
                  </label>
                  <Autocomplete
                    options={cohortTypeOptions}
                    getOptionLabel={(option) => {
                      if (!option) return "";
                      return `${option.name}${
                        option.id ? ` (ID: ${option.id})` : ""
                      }`;
                    }}
                    value={selectedCohortType}
                    onChange={(event, newValue) => {
                      setSelectedCohortType(newValue);
                      props.handleUpdateFranchisee({
                        target: {
                          name: "cohortTypeId",
                          value: newValue ? parseInt(newValue.id) : "",
                        },
                      });
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className={styles.inputField}
                        placeholder="Select cohort type"
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {selectedCohortType && (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedCohortType(null);
                                      props.handleUpdateFranchisee({
                                        target: {
                                          name: "cohortTypeId",
                                          value: "",
                                        },
                                      });
                                    }}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <div className={styles.optionName}>{option.name}</div>
                        <div className={styles.optionId}>ID: {option.id}</div>
                      </li>
                    )}
                    noOptionsText="No cohort types found"
                  />
                </div>

                <div className={styles.formField}>
                  <label className={styles.fieldLabel}>
                    Warehouse <span className={styles.required}>*</span>
                  </label>
                  <Autocomplete
                    options={filteredWarehouses}
                    getOptionLabel={(option) =>
                      option && option.name
                        ? `${option.name} (ID: ${option.id})`
                        : ""
                    }
                    value={selectedWarehouse}
                    onChange={(event, newValue) => {
                      props.handleUpdateFranchisee({
                        target: {
                          name: "warehouseId",
                          value: newValue ? newValue.id : "",
                        },
                      });
                    }}
                    onInputChange={(event, newInputValue) => {
                      setSearchInput(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className={styles.inputField}
                        placeholder="Search by name or ID"
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <div className={styles.optionName}>{option.name}</div>
                        <div className={styles.optionId}>ID: {option.id}</div>
                      </li>
                    )}
                    noOptionsText="No warehouses found"
                  />
                </div>
              </div>

            
              <div className={styles.franchiseeFormBox}>
                <div className={styles.formField}>
                  <label className={styles.fieldLabel}>Cohort ID</label>
                  <TextField
                    fullWidth
                    placeholder="Cohort ID"
                    name="cohortId"
                    value={props.formDataFranchisee?.cohortId || ""}
                    onChange={props.handleUpdateFranchisee}
                    variant="outlined"
                    size="small"
                    className={styles.inputField}
                    disabled
                  />
                </div>

                <div className={styles.formField}>
                  <label className={styles.fieldLabel}>
                    Owner ID <span className={styles.required}>*</span>
                  </label>
                  <TextField
                    fullWidth
                    placeholder="Enter owner ID"
                    name="ownerId"
                    type="number"
                    value={props.formDataFranchisee?.ownerId || ""}
                    onChange={props.handleUpdateFranchisee}
                    variant="outlined"
                    size="small"
                    className={styles.inputField}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ============ ACTION BUTTONS ============ */}
          <div className={styles.franchiseeButtonGroup}>
            <Button
              variant="outlined"
              className={styles.cancelBtn}
              onClick={goback}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className={styles.saveBtn}
              onClick={props.handleApicallToUpdate}
              disabled={props.loading}
            >
              {props.loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FranchiseeUpdateComponent;